import { useState } from 'react'
import { toast } from 'sonner'
import { Copy, ExternalLink, FileText, Link2 } from 'lucide-react'
import { Button, Checkbox, Chip, Input, Textarea, type ChipVariant } from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { documentUrl } from '@/shell/services/cofa-registry/utils/document-url'
import {
  DOC_REQUEST_CREATE_ROLES,
  DOC_REQUEST_REVIEW_ROLES,
  DOC_REQUEST_STATUS_LABEL,
  useDocRequestAction,
  useReviewUpload,
  type DocRequestDetail,
  type DocRequestStatus,
  type DocRequestUpload,
  type DocState,
  type LinkResponse,
} from '../api/doc-requests'

const STATUS_VARIANT: Record<DocRequestStatus, ChipVariant> = {
  open: 'info',
  vendor_submitted: 'warning',
  completed: 'success',
  revoked: 'neutral',
}

const STATE_LABEL: Record<DocState, string> = {
  missing: 'Not sent yet',
  pending: 'To review',
  attached: 'Attached',
  not_available: 'Not available',
  rejected: 'Rejected',
}

const STATE_VARIANT: Record<DocState, ChipVariant> = {
  missing: 'neutral',
  pending: 'warning',
  attached: 'success',
  not_available: 'info',
  rejected: 'danger',
}

function longDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
}

/**
 * The vendor document request behind a Regulatory vendor card: the link,
 * what was asked for per product, and every file the vendor sent with
 * Attach / Reject. Attaching makes the file the product's current document
 * of that type (product document library → Odoo).
 */
export function VendorDocsPanel({ request }: { request: DocRequestDetail }) {
  const canCreate = useHasAnyPermission(DOC_REQUEST_CREATE_ROLES)
  const canReview = useHasAnyPermission(DOC_REQUEST_REVIEW_ROLES)
  const action = useDocRequestAction()
  const [link, setLink] = useState<LinkResponse | null>(null)
  const [reopenNote, setReopenNote] = useState('')
  const [reopening, setReopening] = useState(false)
  const names = Object.fromEntries(request.products.map((p) => [p.product_tmpl_id, p.name]))
  const reviewing = request.status === 'vendor_submitted'

  const run = (kind: 'new-link' | 'reopen' | 'revoke', body?: Record<string, unknown>) =>
    action.mutate({ id: request.id, action: kind, body }, {
      onSuccess: (res) => {
        if ('link' in res) setLink(res)
        setReopening(false)
        setReopenNote('')
      },
      onError: (e) => toast.error((e as Error).message),
    })

  return (
    <section className="flex flex-col gap-4 border-b border-border-subtle bg-surface px-5 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <Link2 size={16} className="text-accent" aria-hidden="true" />
        <h2 className="text-role-section-label">Vendor documents · {request.reference}</h2>
        <Chip size="xs" variant={STATUS_VARIANT[request.status]}>{DOC_REQUEST_STATUS_LABEL[request.status]}</Chip>
        <span className="text-role-metadata text-tertiary">
          {request.progress.settled} of {request.progress.total} settled
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-role-metadata">
        <span className="text-tertiary">Vendor</span>
        <span>{request.vendor_name}{request.contact_name ? ` · ${request.contact_name}` : ''}{request.contact_email ? ` (${request.contact_email})` : ''}</span>
        <span className="text-tertiary">Requested by</span>
        <span>{request.created_by} · {longDate(request.created_at)}</span>
        <span className="text-tertiary">Link</span>
        <span>{request.link_active ? `Open until ${longDate(request.expires_at)}` : request.status === 'open' ? 'Expired' : 'Closed'}</span>
        {request.attestation ? (
          <>
            <span className="text-tertiary">Submitted by</span>
            <span>{request.attestation.name}, {request.attestation.title} · {longDate(request.submitted_at)}</span>
          </>
        ) : null}
      </div>
      {request.attestation?.note ? (
        <p className="rounded-md bg-surface-sunk px-3 py-2 text-sm">Vendor note: {request.attestation.note}</p>
      ) : null}

      <div className="flex flex-col gap-2">
        {request.products.map((p) => (
          <div key={p.product_tmpl_id} className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{p.name}{p.default_code ? ` · ${p.default_code}` : ''}</span>
            {p.documents.map((d) => (
              <Chip key={d.doc_type_key} size="xs" variant={STATE_VARIANT[d.state]}>
                {d.label}: {STATE_LABEL[d.state]}
              </Chip>
            ))}
          </div>
        ))}
      </div>

      {request.uploads.length ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-role-section-label">From the vendor</h3>
          {request.uploads.map((u) => (
            <UploadRow key={u.id} request={request} upload={u} productName={names[u.product_tmpl_id ?? 0] ?? ''}
              canReview={canReview && reviewing} />
          ))}
        </div>
      ) : null}

      {canCreate && (request.status === 'open' || request.status === 'vendor_submitted') ? (
        <div className="flex flex-wrap gap-2">
          {request.status === 'open' ? (
            <Button size="sm" variant="secondary" loading={action.isPending} onClick={() => run('new-link')}>
              New link
            </Button>
          ) : null}
          <Button size="sm" variant="secondary" onClick={() => setReopening((v) => !v)}>Send back to vendor</Button>
          <Button size="sm" variant="ghost" loading={action.isPending}
            onClick={() => window.confirm(`Cancel ${request.reference}? The vendor's link stops working.`) && run('revoke')}>
            Cancel request
          </Button>
        </div>
      ) : null}
      {reopening ? (
        <div className="flex flex-col gap-2">
          <Textarea rows={2} value={reopenNote} maxLength={2000} placeholder="What should the vendor fix or add? (shown on their page)"
            onChange={(e) => setReopenNote(e.target.value)} />
          <div>
            <Button size="sm" variant="primary" disabled={!reopenNote.trim()} loading={action.isPending}
              onClick={() => run('reopen', { note: reopenNote.trim() })}>
              Send back with a new link
            </Button>
          </div>
        </div>
      ) : null}
      {link ? <NewLink link={link} /> : null}
    </section>
  )
}

function UploadRow({
  request,
  upload,
  productName,
  canReview,
}: {
  request: DocRequestDetail
  upload: DocRequestUpload
  productName: string
  canReview: boolean
}) {
  const review = useReviewUpload()
  const [targets, setTargets] = useState<number[]>(upload.product_tmpl_id ? [upload.product_tmpl_id] : [])
  const [issuedOn, setIssuedOn] = useState(upload.issued_on ?? '')
  const [expiresOn, setExpiresOn] = useState(upload.expires_on ?? '')
  const [rejecting, setRejecting] = useState(false)
  const [note, setNote] = useState('')
  const pending = upload.review_status === 'pending'
  const isFile = upload.document_id != null

  const submit = (actionName: 'attach' | 'reject', body: Record<string, unknown>) =>
    review.mutate({ id: request.id, uploadId: upload.id, action: actionName, body }, {
      onSuccess: () => toast.success(actionName === 'attach' ? (isFile ? 'Attached to the product' : 'Accepted') : 'Rejected'),
      onError: (e) => toast.error((e as Error).message),
    })

  return (
    <div className="rounded-md border border-border-default p-3">
      <div className="flex flex-wrap items-center gap-2">
        <FileText size={14} className="text-tertiary" aria-hidden="true" />
        <span className="text-sm font-medium">{upload.doc_type_label}</span>
        <span className="text-sm text-secondary">· {productName}</span>
        {!upload.requested ? <Chip size="xs" variant="accent">Extra</Chip> : null}
        <Chip size="xs" variant={upload.review_status === 'accepted' ? 'success' : upload.review_status === 'rejected' ? 'danger' : 'warning'}>
          {upload.review_status === 'accepted' ? (isFile ? 'Attached' : 'Accepted') : upload.review_status === 'rejected' ? 'Rejected' : 'To review'}
        </Chip>
        {isFile ? (
          <a className="ml-auto inline-flex items-center gap-1 text-sm text-link underline hover:text-link-hover" target="_blank" rel="noreferrer"
            href={documentUrl(upload.document_id!, upload.original_filename)}>
            Open PDF <ExternalLink size={12} />
          </a>
        ) : null}
      </div>
      {!isFile ? <p className="mt-1 text-sm">Not available: {upload.not_available_reason}</p> : null}
      {upload.review_note ? <p className="mt-1 text-sm text-danger-text">Rejected: {upload.review_note}</p> : null}
      {upload.review_status === 'accepted' && upload.attached_to.length ? (
        <p className="mt-1 text-role-metadata text-tertiary">
          Attached to {upload.attached_to.map((a) => request.products.find((p) => p.product_tmpl_id === a.product_tmpl_id)?.name ?? a.product_tmpl_id).join(', ')}
          {upload.reviewed_by ? ` by ${upload.reviewed_by}` : ''}
        </p>
      ) : null}

      {canReview && pending ? (
        <div className="mt-3 flex flex-col gap-2">
          {isFile ? (
            <>
              <span className="text-role-metadata text-tertiary">Attach to</span>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {request.products.map((p) => (
                  <Checkbox key={p.product_tmpl_id} label={p.name} checked={targets.includes(p.product_tmpl_id)}
                    onChange={(e) => setTargets((t) => e.target.checked ? [...t, p.product_tmpl_id]
                      : t.filter((x) => x !== p.product_tmpl_id))} />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-role-metadata text-tertiary">
                  Issued
                  <Input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
                </label>
                <label className="flex flex-col gap-1 text-role-metadata text-tertiary">
                  Expires (blank = the type's default)
                  <Input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
                </label>
              </div>
            </>
          ) : null}
          {rejecting ? (
            <div className="flex flex-col gap-2">
              <Input value={note} maxLength={2000} placeholder="Why? (kept on the card; send back to the vendor to ask again)"
                onChange={(e) => setNote(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" variant="danger" disabled={!note.trim()} loading={review.isPending}
                  onClick={() => submit('reject', { note: note.trim() })}>Reject</Button>
                <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>Back</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="primary" loading={review.isPending} disabled={isFile && !targets.length}
                onClick={() => submit('attach', isFile ? {
                  product_tmpl_ids: targets, issued_on: issuedOn || null, expires_on: expiresOn || null,
                } : {})}>
                {isFile ? `Attach to ${targets.length === 1 ? 'product' : `${targets.length} products`}` : 'Accept'}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setRejecting(true)}>Reject</Button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

function NewLink({ link }: { link: LinkResponse }) {
  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${what} copied`)
    } catch {
      toast.warning('Copy failed; select the text and copy it')
    }
  }
  return (
    <div className="flex flex-col gap-2 rounded-md border border-info-border bg-info-bg p-3">
      <span className="text-sm">New link, shown only now. The old one no longer works. Send it to the vendor from your mailbox.</span>
      <div className="flex items-center gap-2">
        <Input readOnly value={link.link} aria-label="Vendor link" onFocus={(e) => e.currentTarget.select()} />
        <Button size="sm" variant="secondary" onClick={() => void copy(link.link, 'Link')}><Copy size={14} /> Copy</Button>
        <Button size="sm" variant="secondary" onClick={() => void copy(link.suggested_email, 'Email')}><Copy size={14} /> Email</Button>
      </div>
    </div>
  )
}
